import { useState, useEffect } from 'react';

export interface SeoAndHashtagsHook {
  seoKeywords: string[];
  hashtags: string[];
  addSeoKeyword: (keyword: string) => void;
  removeSeoKeyword: (keyword: string) => void;
  addHashtag: (hashtag: string) => void;
  removeHashtag: (hashtag: string) => void;
  predefinedSeoKeywords: string[];
  predefinedHashtags: string[];
}

export const useSeoAndHashtags = (): SeoAndHashtagsHook => {
  const [seoKeywords, setSeoKeywords] = useState<string[]>([]);
  const [hashtags, setHashtags] = useState<string[]>([]);

  // Palabras SEO predefinidas
  const predefinedSeoKeywords = [
    'moda', 'ropa', 'vestidos', 'camisetas', 'pantalones', 'zapatos', 'accesorios',
    'temporada', 'nuevo', 'oferta', 'descuento', 'calidad', 'premium', 'tendencia',
    'estilo', 'elegante', 'cómodo', 'moderno', 'clásico', 'casual', 'formal',
    'verano', 'invierno', 'primavera', 'otoño', 'colores', 'tallas', 'envío gratis'
  ];

  // Hashtags predefinidos
  const predefinedHashtags = [
    '#moda', '#ropa', '#style', '#fashion', '#outfit', '#look', '#trending',
    '#nuevo', '#oferta', '#descuento', '#calidad', '#premium', '#elegante',
    '#cómodo', '#moderno', '#clásico', '#casual', '#formal', '#verano',
    '#invierno', '#primavera', '#otoño', '#colores', '#tallas', '#envíogratis',
    '#shopping', '#compras', '#tienda', '#online', '#delivery', '#envío'
  ];

  // Cargar datos desde localStorage al inicializar
  useEffect(() => {
    const savedSeoKeywords = localStorage.getItem('seoKeywords');
    const savedHashtags = localStorage.getItem('hashtags');
    
    if (savedSeoKeywords) {
      setSeoKeywords(JSON.parse(savedSeoKeywords));
    }
    if (savedHashtags) {
      setHashtags(JSON.parse(savedHashtags));
    }
  }, []);

  // Funciones para manejar SEO Keywords
  const addSeoKeyword = (keyword: string) => {
    if (keyword.trim() && !seoKeywords.includes(keyword.trim())) {
      const newKeywords = [...seoKeywords, keyword.trim()];
      setSeoKeywords(newKeywords);
      localStorage.setItem('seoKeywords', JSON.stringify(newKeywords));
    }
  };

  const removeSeoKeyword = (keyword: string) => {
    const newKeywords = seoKeywords.filter(k => k !== keyword);
    setSeoKeywords(newKeywords);
    localStorage.setItem('seoKeywords', JSON.stringify(newKeywords));
  };

  // Funciones para manejar Hashtags
  const addHashtag = (hashtag: string) => {
    const formattedHashtag = hashtag.startsWith('#') ? hashtag : `#${hashtag}`;
    if (formattedHashtag.trim() && !hashtags.includes(formattedHashtag.trim())) {
      const newHashtags = [...hashtags, formattedHashtag.trim()];
      setHashtags(newHashtags);
      localStorage.setItem('hashtags', JSON.stringify(newHashtags));
    }
  };

  const removeHashtag = (hashtag: string) => {
    const newHashtags = hashtags.filter(h => h !== hashtag);
    setHashtags(newHashtags);
    localStorage.setItem('hashtags', JSON.stringify(newHashtags));
  };

  return {
    seoKeywords,
    hashtags,
    addSeoKeyword,
    removeSeoKeyword,
    addHashtag,
    removeHashtag,
    predefinedSeoKeywords,
    predefinedHashtags
  };
};
